import { useState, type FC, useEffect, type ReactNode } from 'react';
import { LeftSquareFilled, RightSquareFilled } from '@ant-design/icons';
import cx from 'classnames';
import { observer } from 'mobx-react-lite';

import { type WithOptionalDataTest } from 'types';
import { RootStore } from 'store';
import { AiAssistantContent } from './ai-assistant-content.component';
import { NotepadContent } from './notepad-content.component';
import styles from './side-panel.module.scss';

type Props = {
  isOpen?: boolean;
  placeholder?: string;
  children?: ReactNode;
} & WithOptionalDataTest;

export const SidePanel: FC<Props> = observer((props) => {
  const { isOpen = false, placeholder: initialPlaceholder, children, dataTestId } = props;

  const [opened, setOpened] = useState<boolean>(isOpen);

  const { user } = RootStore;

  const isAiEnabled = user?.api_id;

  const placeholder = isAiEnabled ? 'AI assistant' : (initialPlaceholder || 'notepad');

  useEffect(() => {
    setOpened(isOpen);
  }, [isOpen]);

  return (
    <div
      className={cx(
        styles.sidePanel,
        isAiEnabled && styles.sidePanel_aiEnabled,
        opened && styles.sidePanel_opened
      )}
      data-testid={dataTestId}
    >
      {placeholder && (
        <div
          className={cx(styles.sidePanel__placeholder, opened && styles.sidePanel__placeholder_hidden)}
          data-testid="side-panel-placeholder"
          onClick={() => {
            !opened && setOpened(!opened);
          }}
        >
          {placeholder}
        </div>
      )}
      <div
        className={styles.sidePanel__tag}
        data-testid="side-panel-tag"
        onClick={() => {
          setOpened(!opened);
        }}
      >
        {opened ? <RightSquareFilled /> : <LeftSquareFilled />}
      </div>
      <div className={styles.sidePanel__content}>
        {isAiEnabled ? <AiAssistantContent /> : (children || <NotepadContent />)}
      </div>
    </div>
  );
});
